<script setup>
import SmartBox from '../common/SmartBox.vue';
import DestinationSearch from './components/DestinationSearch.vue';
import Occupancy from './components/Occupancy.vue';
import SearchInput from './components/SearchInput.vue';
import BookerCalendar from './components/BookerCalendar.vue';
import GlobalUtil from "../../utils/pricetravel/shared/globalUtil";
import { CalendarAvailability } from "../../utils/pricetravel/shared/calendarAvailability";
import GoogleMapList from "../../utils/maps/GoogleMapList";
import { storeToRefs } from 'pinia'
import { useHotelBookerStore } from '../stores/hotelBooker'
import { ref, getCurrentInstance, onMounted, computed, watch } from 'vue'

const { proxy } = getCurrentInstance()
const __ = proxy.__

const fn = window.__pt.fn;
const config = window.__pt.settings.site;
const _globalUtil = new GlobalUtil();
const defaultDate = fn.getDate();
const box = window.__pt.box || null;

const store = useHotelBookerStore()
const {
  form,
  fieldErrors,
  book,
  recentSearches,
  popularDestinations,
  searchResults,
  algoliaUrl,
  OpenSearch,
  CloseSearch,
  lastQuery,
  localeDatePicker,
  settingPaxes
} = storeToRefs(store)

const guestsBox = ref(null)
const calendarBox = ref(null);
const calendarPrices = ref({});
const isMobile = ref(false);

const calendarDates = computed({
  get() {
    return {
      start: book.value.checkIn ? new Date(book.value.checkIn) : null,
      end: book.value.checkOut ? new Date(book.value.checkOut) : null
    };
  },
  set(val) {
    book.value.checkIn = val.start ? new Date(val.start) : null;
    book.value.checkOut = val.end ? new Date(val.end) : null;
    updateFormDates(book.value.checkIn, book.value.checkOut);
  }
});

function onSelectDates(val) {
  updateFormDates(val.start, val.end);
  book.value.checkIn = val.start;
  book.value.checkOut = val.end;
  if (calendarBox.value) {
    calendarBox.value.close();
  }
}

function updateFormDates(start, end) {
  if (!start) return;
  const pathName = _globalUtil.getFirstPathSegment();
  const format =
    fn.mobileAndTabletCheck() && pathName == "hotel"
      ? localeDatePicker.value.formatMobile
      : localeDatePicker.value.format;

  if (start && end) {
    form.value.dates = `${dayjs(start).format(format).replace(/\./g, '')} - ${dayjs(end).format(format).replace(/\./g, '')}`;
  } else {
    form.value.dates = `${dayjs(start).format(format).replace(/\./g, '')}`;
  }
}

async function fetchAvailability(viewDate) {
  if (!config.showCalendarAvailability) return;
  const placeSelected = book.value?.place_selected;
  if (placeSelected && placeSelected.type == 14 && (placeSelected.code || placeSelected.id)) {
    const targetM = dayjs(viewDate);
    const year = targetM.format('YYYY');
    const month = targetM.format('MM');

    const response = await CalendarAvailability.getAvailability(
      placeSelected.code || placeSelected.id,
      year,
      month
    );
    if (response && response.daysByDate) {
      calendarPrices.value = { ...calendarPrices.value, ...extractPrices(response.daysByDate) };
    }
  }
}

function extractPrices(daysByDate) {
  const prices = {};
  for (const key in daysByDate) {
    const day = daysByDate[key];
    prices[day.checkinFormat] = {
      available: day.available,
      avgPrice: day.available ? (() => {
        const price = roundNumber(day.avgPrice);
        return price != 0 ? `$${price}` : '';
      })() : null,
      color: day.color
    };
  }
  return prices;
}
function roundNumber(val) {

  const currency = __pt.settings.site.currency;
  if (currency === 'COP') {
    const suffix = val.slice(-1);
    let number = parseFloat(val.slice(0, -1));
    return `${customRound(number)}${suffix}`;
  } else if (currency !== 'COP' && (val.endsWith('K') || val.endsWith('M'))) {
    return val;
  } else {
    return customRound(val);
  }
}

function customRound(num) {
  const decimal = num % 1;
  const integer = Math.floor(num);

  if (decimal > 0.5) {
    return integer + 1;
  } else {
    return num;
  }
}
function initDatePicker() {
  let paramsQuery = fn.search();
  if (
    (defaultDate < fn.getDate(book.value.checkOut) ||
      defaultDate >= fn.getDate(book.value.checkOut)) &&
    box
  ) {
    if (paramsQuery.daysToArrival && paramsQuery.daystostay) {
      let dateFirst = new Date();
      dateFirst.setDate(dateFirst.getDate() + parseInt(paramsQuery.daysToArrival));
      book.value.checkIn = dateFirst;
      let dateEnd = new Date(dateFirst);
      dateEnd.setDate(dateEnd.getDate() + parseInt(paramsQuery.daystostay));
      book.value.checkOut = dateEnd;
    } else {
      book.value.checkIn = fn.getDate(box.checkIn);
      book.value.checkOut = fn.getDate(box.checkOut);
    }
  }
  updateFormDates(book.value.checkIn, book.value.checkOut);
}

function onChangePaxes(payload) {
  book.value.paxes = payload || book.value.paxes;
  store.updateInputPaxes();
}
function onApplyPaxes(payload) {
  book.value.paxes = payload || book.value.paxes;
  store.updateInputPaxes();
  isValidFormApply();
  if (guestsBox.value && typeof guestsBox.value.close === 'function') {
    guestsBox.value.close();
  }
}
function onCancelPaxes() {
  store.updateInputPaxes();
  if (guestsBox.value && typeof guestsBox.value.close === 'function') {
    guestsBox.value.close();
  }
}
function onCloseGuests() {
  store.updateInputPaxes();
}

async function onSearch(value, mobile) {
  if (mobile) {
    form.value.destination = value;
  }
  await store.fetchSearchResults(value, mobile);
}

function onOpenSearch() {
  if (!window.__pt.settings.rb) {
    let mockVm = { ln: {} };
    let _googleMapNearby = new GoogleMapList(mockVm, window.axios, null, null);
    _googleMapNearby.getLocationUser();
  }

  setTimeout(() => {
    store.fetchPopularDestinations();
  }, 200);
}

function onSelectDestination(destination) {
  store.selectDestination(destination);
  store.setFieldError('destination', "");
  calendarPrices.value = {};
}
function isValidFormApply() {
  if (store.validateForm(true)) {
    submitSearch();
  }
}
function submitSearch() {
  store.submitSearch();
}

const hasData = computed(() => {
  return (searchResults.value?.length || 0) > 0 ||
    (recentSearches.value?.length || 0) > 0 ||
    (popularDestinations.value?.length || 0) > 0;
})

onMounted(() => {
  store.initialDefaultDate();
  store.initSearchBox();
  initDatePicker();
  store.updateInputPaxes();
  store.loadSearchHistory();
})
</script>

<template>
  <form class="search" @submit.prevent="submitSearch" :aria-label="__('booker.accommodations')">
    <div class="search__inputs">
      <SmartBox @open="onOpenSearch" @isMobile="isMobile = $event" :title="__('booker.where_are_you_going_sub')"
        :OpenBox="OpenSearch" :CloseBox="CloseSearch" :isHeaderActive="false" :hasContent="hasData">
        <template #trigger>
          <SearchInput id="dest" :label="__('booker.where_are_you_going')" :activeFocus="!isMobile"
            :selectOnFocus="!isMobile" :placeholder="__('booker.destination_placeholder')" v-model="form.destination"
            @input="onSearch" :debounce="700" :autocomplete="'off'" :spellcheck="false"
            :error="!!fieldErrors.destination" :errorMessage="fieldErrors.destination" :readonly="isMobile">
            <template #icon>
              <span class="icon font-icons icons-location font-20"></span>
            </template>
          </SearchInput>
        </template>
        <template #content>
          <DestinationSearch :product="'H'" :recentSearches="recentSearches" :popularDestinations="popularDestinations"
            @update:query="onSearch($event, true)" :destination="form.destination" :imgUrl="algoliaUrl"
            :searchResults="searchResults" @update:selectedDestination="onSelectDestination" :lastQuery="lastQuery"
            :label="__('booker.where_are_you_going')" :placeholder="''" :icon="'icons-location'">
          </DestinationSearch>
        </template>
      </SmartBox>

      <SmartBox ref="calendarBox" :title="__('booker.dates')" :isHeaderActive="false" :mobile-breakpoint="768">
        <template #trigger>
          <SearchInput id="calendar-checkIn-hotel" type="text" required name="calendar-checkIn-hotel"
            :activeFocus="true" :label="__('booker.dates')" :readonly="true" v-model="form.dates"
            :error="!!fieldErrors.dates" :errorMessage="fieldErrors.dates">
            <template #icon>
              <span class="icon font-icons icons-calendar-depart font-20"></span>
            </template>
          </SearchInput>
        </template>
        <template #content>
          <BookerCalendar :modelValue="calendarDates" :currency="config.currency" mode="range" :unit="'nights'"
            :minDays="2" :nOffset="-1" :minDate="defaultDate" :prices="calendarPrices" @viewDate="fetchAvailability"
            @select="onSelectDates" @close="calendarBox.close()" />
        </template>
      </SmartBox>

      <SmartBox ref="guestsBox" :title="__('booker.guests_sub')" :isHeaderActive="false" @close="onCloseGuests"
        :mobile-breakpoint="768">
        <template #trigger>
          <SearchInput id="guests" :label="__('booker.guests')" :placeholder="form.guests" :readonly="true"
            :activeFocus="true" v-model="form.guests" :error="!!fieldErrors.guests" :errorMessage="fieldErrors.guests">
            <template #icon>
              <span class="icon font-icons icons-guests font-20"></span>
            </template>
          </SearchInput>
        </template>
        <template #content>
          <Occupancy :product="'H'" :settingPaxes="settingPaxes" :counts="book.paxes" @change="onChangePaxes"
            @apply="onApplyPaxes" @cancel="onCancelPaxes">
          </Occupancy>
        </template>
      </SmartBox>
    </div>

    <div class="search__action">
      <button type="submit" class="search__btn btnPrimary btnPrimary--md">{{ __('booker.button') }}</button>
    </div>
  </form>
</template>

<style scoped lang="scss">
.search {
  display: flex;
  flex-direction: column;
  gap: var(--space-12);
  padding: var(--space-16);
  border-radius: var(--space-12);
  border: 1px solid var(--border-subtle);
  margin: var(--space-16);
  background-color: var(--bg-base);
  box-shadow: var(--shadow-200);

  @media (min-width: 576px) {
    width: 544px;
    margin: var(--space-16) auto;
  }

  @media (min-width: 768px) {
    width: auto;
    border-radius: 0px 0px var(--space-12) var(--space-12);
    display: grid;
    grid-template-areas:
      'inputs inputs'
      'action action';
    grid-template-columns: 1fr 160px;
    padding-inline: 0px;
    gap: var(--space-24);
    box-shadow: none;
    margin: 0px auto;
    border: 0px;
  }

  @media (min-width: 1280px) {
    grid-template-areas:
      'inputs inputs action'
      'filters filters filters';
    width: 100%;
    margin: 0px;
    padding: var(--space-20);
    border-radius: var(--space-12);
    row-gap: 0px;
    column-gap: var(--space-32);
    border: 1px solid var(--border-subtle);
  }

  .icon {
    display: flex;
  }

  &__filters {
    grid-area: filters;
  }

  &__inputs {
    grid-area: inputs;
  }

  &__action {
    grid-area: action;

    @media (min-width: 768px) {
      display: flex;
      justify-content: flex-end;
    }
  }

  &__filters {
    display: flex;
    align-items: center;
  }

  &__inputs {
    display: flex;
    flex-direction: column;
    gap: var(--space-16);

    @media (min-width: 768px) {
      display: grid;
      grid-template-columns: minmax(240px, 2fr) minmax(200px, 1fr) minmax(200px, 1fr);
      gap: var(--space-32);
    }

    @media (min-width: 1280px) {
      grid-template-columns: 356px 1fr 1fr;
    }
  }

  &__btn {
    width: 100%;

    @media (min-width: 768px) {
      width: 100%;
    }

    @media (min-width: 1280px) {
      width: 160px;
    }
  }
}

.group {
  border: none;
  padding: 0px;
  margin: 0px;

  @media (min-width: 768px) {
    display: flex;
    align-items: center;
    gap: var(--space-12);
  }

  &__legend {
    display: none;
    margin: 0px;

    @media (min-width: 768px) {
      display: flex;
      width: fit-content;
    }
  }

  &__list {
    display: flex;
    flex-wrap: wrap;
    gap: var(--space-12);
    margin: 0px;
    padding: 0px;
    list-style: none;
  }
}

.option {
  display: flex;
  align-items: center;
  gap: var(--space-8);
  cursor: pointer;
  margin: 0px;

}
</style>
